import { useMemo } from "react";
import { PSelect, Select } from "./Select";

type PCountriesSelect<T extends object> = {
  listOfValues: T[];
  value: PSelect["value"];
  onChange: PSelect["onChange"];
  label: string;
  color?: string;
  baseColor?: string;
  borderColor?: string;
  expandedBorderColor?: string;
  titleColor?: string;
  onExpandedChange?: (expanded: boolean) => void;
  enableSearch?: boolean;
  description?: string;
  dropdownMaxHeight?: string;
  required?: boolean;
  getSelectCls?: (expanded: boolean) => string;
  fontWeight?: string;
  placeholder?: string;
};

interface IBaseCountries {
  id: string | number;
  country: string;
  countryImg?: string;
}

export const CountriesDropdown = <T extends IBaseCountries>(
  props: PCountriesSelect<T> & { expanded?: boolean }
) => {
  const {
    listOfValues,
    value,
    onExpandedChange,
    onChange,
    label,
    color = "bg-page-body",
    baseColor = "bg-page-body",
    borderColor = "border-border-subtle",
    expandedBorderColor = "border-border-subtle",
    titleColor = "text-text-subtle",
    expanded,
    enableSearch = true,
    description,
    dropdownMaxHeight,
    required = false,
    getSelectCls,
    fontWeight = "font-normal",
    placeholder = "Select country",
  } = props;

  const options = useMemo(() => {
    return listOfValues.map((item) => ({
      label: (
        <div className="flex flex-row justify-between w-full">
          <div className="flex flex-row items-center gap-[4px]">
            {item.countryImg ? (
              <img
                src={item.countryImg}
                alt={item.country}
                height={16}
                width={16}
              />
            ) : null}
            <span className="text-[12px]">{item.country}</span>
          </div>
        </div>
      ),
      value: item.id,
      selectedLabel: (
        <div className="flex flex-row items-center gap-[4px]">
          {item.countryImg ? (
            <img
              src={item.countryImg}
              alt={item.country}
              height={16}
              width={16}
            />
          ) : null}
          <span className="text-[12px]">{item.country}</span>
        </div>
      ),
    }));
  }, [listOfValues]);

  return (
    <div className="flex flex-col space-y-[4px]">
      <Select
        label={label}
        options={options}
        value={value}
        color={color}
        borderColor={borderColor}
        baseColor={baseColor}
        expandedBorderColor={expandedBorderColor}
        titleColor={titleColor}
        expanded={expanded}
        placeholder={placeholder}
        onExpandedChange={onExpandedChange}
        onChange={(val: any) => onChange && onChange(val)}
        getOptionCls={(isActive: any) =>
          isActive
            ? "text-text-navbar-active text-[12px]"
            : "text-text-base text-[12px]"
        }
        fontWeight={fontWeight}
        getSelectCls={getSelectCls}
        enableSearch={enableSearch}
        searchableLabels={listOfValues.map((item) => [item.country])}
        required={required}
        dropdownMaxHeight={dropdownMaxHeight}
      />
      {description && (
        <p className="text-text-subtle text-[10px] font-medium">
          {description}
        </p>
      )}
    </div>
  );
};
